/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 뷰의 상세패널 클래스를 정의한다.
 *
 * @file /src/components/viewer/Panel.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 6. 24.
 */
import * as parent from '@components/panel/Panel';
import { Viewer } from '@components/viewer/Viewer';
import { View } from '@components/viewer/View';
import { List } from '@components/viewer/List';
import { Record } from '@models/store/Record';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 패널 데이터를 업데이트하기 위한 함수
     */
    updater?: (panel: T, record: Record) => Promise<void>;

    /**
     * 이벤트 리스너
     */
    listeners?: Listeners<T>;
}

export class Panel extends parent.Panel {
    /**
     * 패널 데이터를 업데이트하기 위한 함수
     */
    updater: (panel: Panel, record: Record) => Promise<void> = null;

    /**
     * 패널에서 사용하는 데이터 레코드
     */
    record: Record | null = null;

    /**
     * 뷰의 상세패널 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Panel> = null) {
        super({
            ...properties,
            hidden: true,
            resizable: [false, false, false, true],
            border: [false, false, false, true],
        });
        this.title.addControl({
            icon: 'mi mi-close',
            handler: async () => {
                await this.getList().getSelection().deselectAll();
            },
        });

        this.updater = properties.updater ?? null;
    }

    /**
     * 뷰어를 가져온다.
     *
     * @returns 뷰어
     */
    getViewer(): Viewer | null {
        return this.getView().getViewer();
    }

    /**
     * 뷰 패널을 가져온다.
     *
     * @returns 뷰패널
     */
    getView(): View {
        return this.getParent<View>();
    }

    /**
     * 목록패널을 가져온다.
     *
     * @returns 목록패널
     */
    getList(): List {
        return this.getView().getList() ?? null;
    }

    /**
     * 상세패널 크기조절 저장명을 설정한다.
     *
     * @param resizeNm - 상세패널 크기조절 저장명
     */
    setResizeNm(resizeNm: string): void {
        this.resizeNm = resizeNm;
    }

    /**
     * 레코드를 받아 패널의 내용을 업데이트한다.
     *
     * @param record - 레코드
     */
    async update(record: Record): Promise<void> {
        this.record = record;
        await this.updater?.(this, record);
    }

    /**
     * 패널에서 사용하는 데이터 레코드를 가져온다.
     *
     * @return 패널에서 사용하는 데이터 레코드
     */
    getRecord(): Record | null {
        return this.record;
    }

    /**
     * 리사이저에 의해 리사이즈되었을 때
     *
     * @param width - 변경된 가로크기
     * @param height - 변경된 세로크기
     */
    async onResize(width: number, height: number): Promise<void> {
        for (const view of (this.getViewer()?.getItems() ?? []) as View[]) {
            view.getPanel().setWidth(width);
        }

        await super.onResize(width, height);
    }
}
